import React from 'react';
import { formatNumber, parseSpanishNumber, formatDate, parseDate } from '../../utils/constants';

function GenericItemsTable({ title, itemsKey, fields, columnOrder, totalsConfig, data, onChange }) {
  const items = data[itemsKey] || [];
  const fieldEntries = columnOrder
    ? columnOrder.filter(k => k in fields).map(k => [k, fields[k]])
    : Object.entries(fields);

  const isNumericField = (key) => {
    const firstItem = items[0];
    if (!firstItem) return false;
    return typeof firstItem[key] === 'number';
  };

  const handleItemChange = (index, field, value, isNumber) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: isNumber ? parseSpanishNumber(value) : parseDate(value) };
    onChange({ [itemsKey]: newItems });
  };

  const handleTotalChange = (field, value) => {
    onChange({ [field]: parseSpanishNumber(value) });
  };

  const renderInput = (value, onInputChange, isNumber) => (
    <input
      type="text"
      className={`table-input ${isNumber ? 'number' : ''}`}
      value={isNumber ? formatNumber(value) : (formatDate(value) ?? '')}
      onChange={(e) => onInputChange(e.target.value)}
      placeholder="-"
    />
  );

  const renderTotalInput = (value, onInputChange) => (
    <input
      type="text"
      className="total-input"
      value={formatNumber(value)}
      onChange={(e) => onInputChange(e.target.value)}
      placeholder="-"
    />
  );

  return (
    <div className="line-items-container">
      <div className="table-header">
        <h3>{title}</h3>
      </div>

      {items.length === 0 ? (
        <div className="table-empty">
          <p>No se encontraron elementos</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="items-table">
            <thead>
              <tr>
                {fieldEntries.map(([key, label]) => (
                  <th key={key} className={isNumericField(key) ? 'number' : ''}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={index}>
                  {fieldEntries.map(([key, label]) => {
                    const isNumber = isNumericField(key);
                    return (
                      <td key={key} className={isNumber ? 'number' : ''}>
                        {renderInput(item[key], (v) => handleItemChange(index, key, v, isNumber), isNumber)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalsConfig && (
        <div className="totales-section">
          {Object.entries(totalsConfig.fields).map(([key, label], i, arr) => (
            <div key={key} className={`total-row ${i === arr.length - 1 ? 'final' : ''}`}>
              <span>{label}:</span>
              <span className="total-value">
                {renderTotalInput(data[key], (v) => handleTotalChange(key, v))}
              </span>
            </div>
          ))}
        </div>
      )}

      <style>{`
        .line-items-container {
          background: var(--card-background);
          border-radius: var(--border-radius);
          box-shadow: var(--shadow);
          overflow: hidden;
        }
        .table-header {
          padding: 12px 16px;
          background: #374151;
          color: white;
        }
        .table-header h3 {
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .table-empty {
          padding: 32px;
          text-align: center;
          color: var(--text-secondary);
        }
        .table-wrapper {
          overflow-x: auto;
        }
        .items-table {
          width: 100%;
          border-collapse: collapse;
        }
        .items-table th,
        .items-table td {
          padding: 8px 10px;
          text-align: left;
          border-bottom: 1px solid var(--border-color);
          vertical-align: top;
        }
        .items-table th {
          background: #f9fafb;
          font-size: 11px;
          font-weight: 600;
          color: var(--text-secondary);
          text-transform: uppercase;
        }
        .items-table td {
          font-size: 13px;
        }
        .items-table .number {
          text-align: right;
        }
        .items-table tr:hover {
          background: #f9fafb;
        }
        .totales-section {
          padding: 16px;
          background: #f9fafb;
          border-top: 2px solid var(--border-color);
        }
        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 8px 0;
          font-size: 14px;
        }
        .total-row.final {
          font-weight: 600;
          font-size: 16px;
          padding-top: 12px;
          margin-top: 8px;
          border-top: 1px solid var(--border-color);
        }
        .total-value {
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .table-input {
          width: 100%;
          padding: 6px 8px;
          font-size: 14px;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          transition: border-color 0.2s, background 0.2s;
        }
        .table-input:hover {
          background: #f3f4f6;
        }
        .table-input:focus {
          outline: none;
          border-color: var(--primary-color);
          background: #fff;
        }
        .table-input.number {
          text-align: right;
        }
        .table-input::placeholder {
          color: var(--text-secondary);
        }
        .total-input {
          width: 80px;
          padding: 4px 8px;
          font-size: inherit;
          font-weight: inherit;
          border: 1px solid transparent;
          border-radius: 4px;
          background: transparent;
          text-align: right;
          transition: border-color 0.2s, background 0.2s;
        }
        .total-input:hover {
          background: #e5e7eb;
        }
        .total-input:focus {
          outline: none;
          border-color: var(--primary-color);
          background: #fff;
        }
        .total-input::placeholder {
          color: var(--text-secondary);
        }
      `}</style>
    </div>
  );
}

export default GenericItemsTable;
